<template>
  <span class="tutorials-list">
    <v-btn
      icon
      :ripple="false"
      class="tutorials-list__launcher text-body ma-2"
      :class="{ 'btn-dark-hover': !hasBg, 'btn-hover': hasBg }"
      :color="linkColor"
      small
      :title="$t('Caju_assistente_virtual')"
      :aria-label="$t('Caju_assistente_virtual')"
      @click="openDialog"
    >
      <span class="tutorials-list__logo-icon" aria-hidden="true">
        <img :src="cajupeLogo" alt="" />
      </span>
    </v-btn>

    <v-dialog
      v-if="dialog"
      v-model="dialog"
      max-width="980"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      :retain-focus="false"
      hide-overlay
      content-class="tutorials-list__dialog"
    >
      <v-card class="tutorials-list__card border-radius-xl">
        <div class="tutorials-list__header px-4 py-4">
          <div class="tutorials-list__hero">
            <div class="tutorials-list__agent-mark" aria-hidden="true">
              <img :src="cajupeLogo" alt="" />
            </div>
            <div class="tutorials-list__hero-copy">
              <div class="tutorials-list__title-row">
                <div class="tutorials-list__brand">Caju</div>
                <span class="tutorials-list__status">
                  <span class="tutorials-list__status-dot"></span>
                  {{ $t("CAJU_ia_ativa") }}
                </span>
              </div>
              <p class="tutorials-list__description mb-0">
                {{ cajuDescription }}
              </p>
              <div class="tutorials-list__metrics">
                <span>
                  <strong>{{ visibleTutorials.length }}</strong>
                  {{ $t("Processos_disponiveis") }}
                </span>
                <span>
                  <strong>{{ currentRouteCount }}</strong>
                  {{ $t("CAJU_na_tela_atual") }}
                </span>
              </div>
            </div>
          </div>
          <div class="tutorials-list__actions">
            <v-btn
              v-if="canManageTutorials"
              text
              small
              class="text-capitalize tutorials-list__manage"
              :to="{ name: 'TutorialProcessos' }"
              @click="dialog = false"
            >
              <v-icon size="16" left>mdi-tune-variant</v-icon>
              {{ $t("Gerenciar_tutoriais") }}
            </v-btn>
            <v-btn icon :ripple="false" @click="dialog = false">
              <v-icon size="18">fas fa-times</v-icon>
            </v-btn>
          </div>
        </div>

        <v-card-text class="tutorials-list__body px-4 pb-4">
          <div class="tutorials-list__mode-switch mb-3">
            <v-btn-toggle
              v-model="panel"
              mandatory
              dense
              class="tutorials-list__mode-toggle"
            >
              <v-btn value="support" small @click.stop="openSupport">
                <span class="tutorials-list__tab-logo" aria-hidden="true">
                  <img :src="cajupeLogo" alt="" />
                </span>
                {{ $t("CAJU_assistente") }}
              </v-btn>
              <v-btn value="tutorials" small>
                <v-icon size="16" left>mdi-format-list-checks</v-icon>
                {{ $t("CAJU_processos_guiados") }}
              </v-btn>
            </v-btn-toggle>
          </div>

          <div v-show="panel === 'chat'" class="tutorials-list__chat-shell">
            <div class="tutorials-list__chat-header">
              <div>
                <div class="tutorials-list__chat-title">
                  {{ $t("CAJU_chat_titulo") }}
                </div>
                <div class="tutorials-list__chat-subtitle">
                  {{ $t("CAJU_chat_subtitulo") }}
                </div>
              </div>
              <v-btn
                text
                small
                class="text-capitalize tutorials-list__chat-processes"
                @click="panel = 'tutorials'"
              >
                <v-icon size="16" left>mdi-compass-outline</v-icon>
                {{ $t("CAJU_ver_processos") }}
              </v-btn>
            </div>

            <div ref="chatMessages" class="tutorials-list__chat-messages">
              <div
                v-for="message in chatMessages"
                :key="message.id"
                class="tutorials-list__chat-message"
                :class="`tutorials-list__chat-message--${message.role}`"
              >
                <div class="tutorials-list__chat-avatar" aria-hidden="true">
                  <v-icon v-if="message.role === 'user'" size="16">
                    mdi-account
                  </v-icon>
                  <img v-else :src="cajupeLogo" alt="" />
                </div>
                <div class="tutorials-list__chat-bubble">
                  <div class="tutorials-list__chat-text">
                    {{ message.text }}
                  </div>

                  <div
                    v-if="
                      message.suggestedTutorials &&
                      message.suggestedTutorials.length
                    "
                    class="tutorials-list__chat-suggestions"
                  >
                    <div class="tutorials-list__chat-label">
                      {{ $t("CAJU_chat_processos_sugeridos") }}
                    </div>
                    <div class="tutorials-list__chat-suggestion-list">
                      <v-btn
                        v-for="tutorial in message.suggestedTutorials"
                        :key="tutorial.slug"
                        small
                        outlined
                        class="text-capitalize tutorials-list__chat-suggestion"
                        @click.stop="launchSuggestedTutorial(tutorial)"
                      >
                        <v-icon size="15" left>
                          {{ itemIcon(tutorial) }}
                        </v-icon>
                        {{ tutorial.title }}
                      </v-btn>
                    </div>
                  </div>

                  <v-btn
                    v-if="message.supportUrl"
                    :href="message.supportUrl"
                    target="_blank"
                    rel="noopener"
                    small
                    depressed
                    class="text-capitalize tutorials-list__support-link"
                  >
                    <v-icon size="15" left>mdi-headset</v-icon>
                    {{ $t("CAJU_chat_abrir_chamado") }}
                  </v-btn>
                </div>
              </div>

              <div v-if="chatLoading" class="tutorials-list__chat-message">
                <div class="tutorials-list__chat-avatar" aria-hidden="true">
                  <img :src="cajupeLogo" alt="" />
                </div>
                <div
                  class="
                    tutorials-list__chat-bubble
                    tutorials-list__chat-bubble--loading
                  "
                >
                  <span class="tutorials-list__thinking" aria-hidden="true">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                  <span>{{ $t("CAJU_chat_consultando") }}</span>
                </div>
              </div>
            </div>

            <div class="tutorials-list__chat-composer">
              <v-textarea
                v-model="chatInput"
                rows="2"
                auto-grow
                outlined
                dense
                hide-details
                :disabled="chatLoading"
                :placeholder="$t('CAJU_chat_placeholder')"
                @keydown.enter.exact.prevent="sendAiChat"
              ></v-textarea>
              <v-btn
                depressed
                color="primary"
                class="text-capitalize tutorials-list__chat-send"
                :loading="chatLoading"
                :disabled="!chatInput.trim()"
                @click="sendAiChat"
              >
                <v-icon size="16" left>mdi-send</v-icon>
                {{ $t("CAJU_chat_enviar") }}
              </v-btn>
            </div>
          </div>

          <div
            v-show="panel === 'tutorials'"
            class="tutorials-list__process-panel"
          >
            <div class="tutorials-list__search-panel">
              <v-row class="align-center">
                <v-col cols="12" md="7">
                  <v-text-field
                    v-model="search"
                    dense
                    outlined
                    hide-details
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    class="tutorials-list__search-input"
                    :label="$t('Pesquisar_tutoriais')"
                    :placeholder="$t('CAJU_busca_placeholder')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="5">
                  <v-btn-toggle
                    v-model="scope"
                    mandatory
                    dense
                    class="tutorials-list__scope"
                  >
                    <v-btn value="all" small>
                      <v-icon size="15" left>mdi-view-grid-outline</v-icon>
                      {{ $t("Todos") }}
                    </v-btn>
                    <v-btn value="current" small>
                      <v-icon size="15" left>mdi-crosshairs-gps</v-icon>
                      {{ $t("Tela_atual") }}
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>

              <div class="tutorials-list__category-rail">
                <v-chip
                  small
                  :outlined="!!category"
                  :class="{ 'tutorials-list__category--active': !category }"
                  @click="category = ''"
                >
                  {{ $t("CAJU_todas_categorias") }}
                </v-chip>
                <v-chip
                  v-for="item in categories"
                  :key="item"
                  small
                  :outlined="category !== item"
                  :class="{
                    'tutorials-list__category--active': category === item,
                  }"
                  @click="category = item"
                >
                  {{ item }}
                </v-chip>
                <v-btn
                  v-if="hasActiveFilters"
                  text
                  x-small
                  class="text-capitalize tutorials-list__clear"
                  @click="clearFilters"
                >
                  <v-icon size="14" left>mdi-filter-remove-outline</v-icon>
                  {{ $t("Limpar") }}
                </v-btn>
              </div>
            </div>

            <v-progress-linear
              v-if="loading"
              indeterminate
              color="primary"
              class="mt-4"
            ></v-progress-linear>

            <div v-if="!loading" class="tutorials-list__result-row">
              <span>{{ resultsSummary }}</span>
              <span v-if="searchTerms.length">
                {{ $t("Filtros") }}: {{ searchTerms.join(", ") }}
              </span>
            </div>

            <div
              v-if="!loading && orderedTutorials.length === 0"
              class="tutorials-list__empty"
            >
              <span class="tutorials-list__empty-logo" aria-hidden="true">
                <img :src="cajupeLogo" alt="" />
              </span>
              <div>{{ $t("Nenhum_tutorial_disponivel") }}</div>
            </div>

            <v-list
              v-if="!loading && orderedTutorials.length > 0"
              two-line
              class="tutorials-list__items mt-3"
            >
              <template v-for="(item, index) in orderedTutorials">
                <v-list-item
                  :key="item.slug || item.id || index"
                  class="tutorials-list__item"
                  :class="{
                    'tutorials-list__item--current':
                      isCurrentRouteTutorial(item),
                  }"
                  @click="abrirProcesso(item)"
                >
                  <v-list-item-avatar
                    class="tutorials-list__item-avatar"
                    size="44"
                  >
                    <v-icon>{{ itemIcon(item) }}</v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title class="text-sm font-weight-bolder">
                      {{ item.title }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ item.subtitle || item.category }}
                    </v-list-item-subtitle>
                    <div class="tutorials-list__chips">
                      <v-chip
                        v-if="isCurrentRouteTutorial(item)"
                        x-small
                        color="success"
                        text-color="white"
                      >
                        {{ $t("Tela_atual") }}
                      </v-chip>
                      <v-chip v-if="item.category" x-small outlined>
                        {{ item.category }}
                      </v-chip>
                      <v-chip v-if="item.hasStaticHtml" x-small outlined>
                        {{ $t("Tutorial_html_curto") }}
                      </v-chip>
                      <v-chip
                        v-if="passosDoProcesso(item).length"
                        x-small
                        outlined
                      >
                        {{ stepCountLabel(item) }}
                      </v-chip>
                    </div>
                  </v-list-item-content>

                  <v-list-item-action class="tutorials-list__item-actions">
                    <v-btn
                      v-if="item.hasStaticHtml"
                      icon
                      :ripple="false"
                      :title="$t('Ler_tutorial')"
                      @click.stop="lerTutorial(item)"
                    >
                      <v-icon size="20"
                        >mdi-book-open-page-variant-outline</v-icon
                      >
                    </v-btn>
                    <v-btn
                      v-if="passosDoProcesso(item).length"
                      icon
                      :ripple="false"
                      :loading="launchingSlug === item.slug"
                      :title="$t('Iniciar')"
                    >
                      <v-icon size="20">mdi-play-circle-outline</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
                <v-divider
                  v-if="index < orderedTutorials.length - 1"
                  :key="`${item.slug || index}-divider`"
                ></v-divider>
              </template>
            </v-list>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <TutorialVisualizador
      v-model="lendo"
      :tutorial-id="leitura ? leitura.id : null"
      :titulo="leitura ? leitura.title : ''"
    />

    <v-snackbar v-model="snackbar.show" timeout="4000" color="error">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          {{ $t("Fechar") }}
        </v-btn>
      </template>
    </v-snackbar>
  </span>
</template>

<script>
import TutorialDataService, {
  normalizePermission,
  normalizeTutorialProcess,
  parseJsonList,
} from "@/services/tutorials/TutorialDataService";
import cajupeLogo from "@/assets/Cajupe_simples.png";
import TutorialVisualizador from "@/components/tutorials/TutorialVisualizador.vue";
import { passosDoProcesso, processoDaTela } from "@/utils/tutoriais";

const normalizeSearch = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export default {
  name: "TutorialsList",

  components: { TutorialVisualizador },

  props: {
    hasBg: {
      type: Boolean,
      default: false,
    },
    linkColor: {
      type: String,
      default: "",
    },
    positionRelative: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      dialog: false,
      loading: false,
      panel: "tutorials",
      search: "",
      category: "",
      scope: "all",
      tutorials: [],
      launchingSlug: "",
      lendo: false,
      leitura: null,
      chatInput: "",
      chatLoading: false,
      chatMessages: [],
      chatSequence: 0,
      chatSupportTicketId: null,
      cajupeLogo,
      snackbar: {
        show: false,
        text: "",
      },
    };
  },

  computed: {
    permissions() {
      const permissoes = this.$store?.state?.permissoes;
      return Array.isArray(permissoes) ? permissoes : [];
    },

    canManageTutorials() {
      return [
        "ROLE_Suporte_Atendimento",
        "ROLE_TutorialProcess_List",
        "ROLE_TutorialProcess_Insert",
        "ROLE_TutorialProcess_Update",
      ].some((permission) => this.permissions.includes(permission));
    },

    cajuDescription() {
      return this.$t("CAJU_descricao_servico");
    },

    categories() {
      return this.visibleTutorials
        .map((item) => item.category)
        .filter(Boolean)
        .filter((item, index, list) => list.indexOf(item) === index)
        .sort((a, b) => a.localeCompare(b));
    },

    currentRouteCount() {
      return this.visibleTutorials.filter((item) =>
        this.isCurrentRouteTutorial(item)
      ).length;
    },

    searchTerms() {
      return normalizeSearch(this.search).split(/\s+/).filter(Boolean);
    },

    hasActiveFilters() {
      return Boolean(
        this.searchTerms.length || this.category || this.scope !== "all"
      );
    },

    resultsSummary() {
      const count = this.orderedTutorials.length;
      const key =
        count === 1
          ? "CAJU_resultado_encontrado"
          : "CAJU_resultados_encontrados";
      return `${count} ${this.$t(key)}`;
    },

    visibleTutorials() {
      return this.tutorials
        .map(normalizeTutorialProcess)
        .filter((item) => item.active !== false)
        .filter((item) => this.hasAllPermissions(item.requiredPermissions));
    },

    filteredTutorials() {
      const category = normalizeSearch(this.category);

      return this.visibleTutorials.filter((item) => {
        if (this.scope === "current" && !this.isCurrentRouteTutorial(item)) {
          return false;
        }

        if (category && normalizeSearch(item.category) !== category) {
          return false;
        }

        if (!this.searchTerms.length) return true;

        const haystack = this.tutorialSearchHaystack(item);
        return this.searchTerms.every((term) => haystack.includes(term));
      });
    },

    orderedTutorials() {
      return [...this.filteredTutorials].sort((a, b) => {
        if (this.searchTerms.length) {
          const scoreA = this.tutorialSearchScore(a);
          const scoreB = this.tutorialSearchScore(b);
          if (scoreA !== scoreB) return scoreB - scoreA;
        }

        const routeA = this.isCurrentRouteTutorial(a) ? 0 : 1;
        const routeB = this.isCurrentRouteTutorial(b) ? 0 : 1;
        if (routeA !== routeB) return routeA - routeB;
        return Number(a.displayOrder || 0) - Number(b.displayOrder || 0);
      });
    },
  },

  methods: {
    async openDialog() {
      this.dialog = true;
      await this.loadTutorials();
    },

    openSupport() {
      this.dialog = false;
      this.$nextTick(() => {
        this.panel = "tutorials";
      });
      if (this.$route?.name !== "Suporte") {
        this.$router.push({ name: "Suporte" });
      }
    },

    async loadTutorials() {
      this.loading = true;
      try {
        const response = await TutorialDataService.available();
        const remoteTutorials = Array.isArray(response?.data)
          ? response.data.map(normalizeTutorialProcess)
          : [];
        this.tutorials = remoteTutorials;
      } catch (error) {
        this.tutorials = [];
        this.showError(this.$t("Falha_ao_listar_tutoriais"));
      } finally {
        this.loading = false;
      }
    },

    ensureInitialChatMessage() {
      if (this.chatMessages.length) return;
      this.pushChatMessage({
        role: "assistant",
        text: this.$t("CAJU_chat_mensagem_inicial"),
      });
    },

    async sendAiChat() {
      const message = String(this.chatInput || "").trim();
      if (!message || this.chatLoading) return;

      const history = this.buildChatHistoryPayload();
      this.pushChatMessage({ role: "user", text: message });
      this.chatInput = "";
      this.chatLoading = true;
      this.scrollChatToBottom();

      try {
        const response = await TutorialDataService.chat({
          message,
          history,
          currentRouteName: this.$route?.name,
          currentRoutePath: this.$route?.path,
          supportTicketId: this.chatSupportTicketId,
        });
        const data = response?.data || {};
        if (data.supportTicketId) {
          this.chatSupportTicketId = data.supportTicketId;
        }
        const answerable = data.answerable === true;
        this.pushChatMessage({
          role: "assistant",
          text: answerable
            ? data.answer || ""
            : data.fallbackMessage || this.$t("CAJU_chat_falha"),
          answerable,
          suggestedTutorials: Array.isArray(data.suggestedTutorials)
            ? data.suggestedTutorials
            : [],
          supportUrl: answerable ? "" : data.supportUrl,
        });
      } catch (error) {
        this.pushChatMessage({
          role: "assistant",
          text: this.$t("CAJU_chat_falha"),
          answerable: false,
          supportUrl: "https://appcajupe.com/#/dashboard/suporte",
        });
      } finally {
        this.chatLoading = false;
        this.scrollChatToBottom();
      }
    },

    buildChatHistoryPayload() {
      return this.chatMessages
        .slice(-8)
        .filter((message) => message && message.text)
        .map((message) => ({
          role: message.role === "user" ? "user" : "assistant",
          content: message.text,
        }));
    },

    pushChatMessage(message) {
      this.chatMessages.push({
        id: ++this.chatSequence,
        role: message.role || "assistant",
        text: message.text || "",
        answerable: message.answerable !== false,
        suggestedTutorials: message.suggestedTutorials || [],
        supportUrl: message.supportUrl || "",
      });
    },

    scrollChatToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatMessages;
        if (el && typeof el.scrollTop === "number") {
          el.scrollTop = el.scrollHeight;
        }
      });
    },

    hasAllPermissions(requiredPermissions = []) {
      const required = Array.isArray(requiredPermissions)
        ? requiredPermissions
        : parseJsonList(requiredPermissions, []);

      return required
        .map(normalizePermission)
        .filter(Boolean)
        .every((permission) => this.permissions.includes(permission));
    },

    isCurrentRouteTutorial(item) {
      return processoDaTela(item, this.$route || {});
    },

    passosDoProcesso,

    normalizePath(value) {
      return String(value || "")
        .split("?")[0]
        .replace(/\/+$/, "")
        .toLowerCase();
    },

    clearFilters() {
      this.search = "";
      this.category = "";
      this.scope = "all";
    },

    itemIcon(item) {
      const icon = String(item?.icon || "").trim();
      if (!icon || icon.includes("lightbulb")) return "mdi-map-marker-path";
      return icon;
    },

    launchSuggestedTutorial(tutorial) {
      const slug = String(tutorial?.slug || "").toLowerCase();
      const item = this.visibleTutorials.find(
        (candidate) => String(candidate.slug || "").toLowerCase() === slug
      );
      if (!item) {
        this.showError(this.$t("Tutorial_indisponivel"));
        return;
      }
      this.launchTutorial(item);
    },

    tutorialSearchHaystack(item) {
      const steps = Array.isArray(item.steps)
        ? item.steps
        : parseJsonList(item.stepsJson, []);

      const stepFields = steps.reduce(
        (fields, step) => [
          ...fields,
          step.boxTitle,
          step.title,
          step.boxText,
          step.text,
          step.routeName,
          step.routePath,
          step.selector,
          ...(Array.isArray(step.selectors) ? step.selectors : []),
        ],
        []
      );

      return normalizeSearch(
        [
          item.title,
          item.subtitle,
          item.category,
          item.slug,
          ...(item.keywords || []),
          ...stepFields,
        ].join(" ")
      );
    },

    tutorialSearchScore(item) {
      const fields = {
        title: normalizeSearch(item.title),
        subtitle: normalizeSearch(item.subtitle),
        category: normalizeSearch(item.category),
        slug: normalizeSearch(item.slug),
        keywords: normalizeSearch((item.keywords || []).join(" ")),
        haystack: this.tutorialSearchHaystack(item),
      };

      return this.searchTerms.reduce(
        (score, term) => {
          if (fields.title.includes(term)) score += 36;
          if (fields.keywords.includes(term)) score += 28;
          if (fields.subtitle.includes(term)) score += 18;
          if (fields.category.includes(term)) score += 14;
          if (fields.slug.includes(term)) score += 10;
          if (fields.haystack.includes(term)) score += 4;
          return score;
        },
        this.isCurrentRouteTutorial(item) ? 20 : 0
      );
    },

    stepCountLabel(item) {
      const steps = Array.isArray(item.steps)
        ? item.steps
        : parseJsonList(item.stepsJson, []);
      const count = this.countVisibleTutorialSteps(steps);
      return count === 1
        ? this.$t("Um_passo")
        : `${count} ${this.$t("Passos")}`;
    },

    countVisibleTutorialSteps(steps = []) {
      let currentRoute = {
        name: this.$route?.name,
        path: this.$route?.path,
      };

      const count = steps.reduce((total, step) => {
        const stepCount = this.visibleTutorialStepCount(step, currentRoute);
        currentRoute = this.resolveNextTutorialRoute(currentRoute, step);
        return total + stepCount;
      }, 0);

      return count || steps.length;
    },

    visibleTutorialStepCount(step, currentRoute) {
      if (!step || typeof step !== "object") return 0;

      if (step.navigationOnly === true && !this.hasStepSelector(step)) {
        if (step.countStep === true) return 1;
        return this.shouldGuideMenuNavigation(step, currentRoute) ? 2 : 0;
      }

      return 1;
    },

    hasStepSelector(step) {
      if (String(step.selector || "").trim()) return true;
      return (
        Array.isArray(step.selectors) &&
        step.selectors.some((selector) => String(selector || "").trim())
      );
    },

    shouldGuideMenuNavigation(step, currentRoute) {
      if (step.guideMenuNavigation === false) return false;

      const route = this.resolveTutorialStepRoute(step);
      if (!route.path) return false;

      const shouldNavigateByName =
        route.name && currentRoute.name !== route.name;
      const shouldNavigateByPath =
        route.path &&
        this.normalizePath(currentRoute.path) !==
          this.normalizePath(route.path);

      return Boolean(shouldNavigateByName || shouldNavigateByPath);
    },

    resolveNextTutorialRoute(currentRoute, step) {
      const route = this.resolveTutorialStepRoute(step);
      if (!route.name && !route.path) return currentRoute;

      return {
        ...currentRoute,
        name: route.name || currentRoute.name,
        path: route.path || currentRoute.path,
      };
    },

    resolveTutorialStepRoute(step) {
      const routeName = step.routeName || step.route;
      const routePath = this.resolveTutorialRoutePath(
        routeName,
        step.routePath || step.path
      );

      return { name: routeName, path: routePath };
    },

    resolveTutorialRoutePath(routeName, routePath) {
      if (routePath) return routePath;
      if (!routeName || typeof this.$router?.resolve !== "function") return "";

      try {
        const resolved = this.$router.resolve({ name: routeName });
        return resolved?.route?.path || resolved?.href || "";
      } catch (error) {
        return "";
      }
    },

    // A linha abre o passo a passo; processo só com o tutorial em HTML abre a leitura.
    abrirProcesso(item) {
      if (!passosDoProcesso(item).length && item.hasStaticHtml) {
        this.lerTutorial(item);
        return;
      }
      this.launchTutorial(item);
    },

    async lerTutorial(item) {
      this.dialog = false;
      await this.$nextTick();
      await this.waitForDialogTransition();
      this.leitura = item;
      this.lendo = true;
    },

    async launchTutorial(item) {
      const tutorial = normalizeTutorialProcess(item);
      const steps = Array.isArray(tutorial.steps)
        ? tutorial.steps
        : parseJsonList(tutorial.stepsJson, []);

      if (!steps.length) {
        this.showError(this.$t("Tutorial_sem_passos"));
        return;
      }

      this.launchingSlug = tutorial.slug;
      this.dialog = false;

      try {
        await this.$nextTick();
        await this.waitForDialogTransition();
        await this.$tutorials.start(steps);
      } catch (error) {
        this.showError(this.$t("Nao_foi_possivel_iniciar_tutorial"));
      } finally {
        this.launchingSlug = "";
      }
    },

    waitForDialogTransition() {
      return new Promise((resolve) => setTimeout(resolve, 340));
    },

    showError(text) {
      this.snackbar = {
        show: true,
        text,
      };
    },
  },
};
</script>

<style scoped>
.tutorials-list {
  display: inline-flex;
  align-items: center;
}

.tutorials-list__launcher {
  background: rgba(20, 184, 166, 0.1) !important;
  border: 1px solid rgba(20, 184, 166, 0.24);
  color: #0f766e !important;
  opacity: 0.92;
  transition: background 160ms ease, border-color 160ms ease, opacity 160ms ease;
}

.tutorials-list__launcher:hover,
.tutorials-list__launcher:focus {
  background: rgba(20, 184, 166, 0.16) !important;
  border-color: rgba(20, 184, 166, 0.38);
  opacity: 1;
}

.tutorials-list__logo-icon {
  align-items: center;
  background: rgba(255, 255, 255, 0.82);
  border-radius: 999px;
  display: inline-flex;
  height: 24px;
  justify-content: center;
  overflow: hidden;
  width: 24px;
}

.tutorials-list__logo-icon img {
  display: block;
  height: 21px;
  object-fit: contain;
  width: 21px;
}

.tutorials-list__card {
  background: #f7faf8;
  border: 1px solid rgba(15, 35, 39, 0.1);
  box-shadow: 0 24px 60px rgba(15, 35, 39, 0.18);
  color: #18232b;
  overflow: hidden;
}

.tutorials-list__header {
  align-items: flex-start;
  background: linear-gradient(135deg, #10252a 0%, #164340 54%, #806f38 150%);
  border-bottom: 1px solid rgba(20, 184, 166, 0.24);
  color: #ffffff;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}

.tutorials-list__hero {
  align-items: center;
  display: flex;
  gap: 14px;
  min-width: 0;
}

.tutorials-list__agent-mark {
  align-items: center;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.62);
  border-radius: 8px;
  box-shadow: 0 16px 32px rgba(4, 18, 22, 0.28),
    0 0 0 6px rgba(242, 193, 78, 0.08);
  display: inline-flex;
  flex: 0 0 auto;
  height: 52px;
  justify-content: center;
  overflow: hidden;
  width: 52px;
}

.tutorials-list__agent-mark img {
  display: block;
  height: 42px;
  object-fit: contain;
  width: 42px;
}

.tutorials-list__hero-copy {
  min-width: 0;
}

.tutorials-list__title-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tutorials-list__brand {
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1.2;
}

.tutorials-list__status {
  align-items: center;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  color: #e9fffb;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 700;
  gap: 6px;
  letter-spacing: 0;
  padding: 4px 9px;
}

.tutorials-list__status-dot {
  background: #f2c14e;
  border-radius: 999px;
  box-shadow: 0 0 0 4px rgba(242, 193, 78, 0.18);
  display: inline-flex;
  height: 7px;
  width: 7px;
}

.tutorials-list__description {
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.88rem;
  line-height: 1.45;
  margin-top: 7px;
  max-width: 620px;
}

.tutorials-list__metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.tutorials-list__metrics span {
  align-items: baseline;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.78);
  display: inline-flex;
  font-size: 0.76rem;
  gap: 5px;
  padding: 5px 8px;
}

.tutorials-list__metrics strong {
  color: #ffffff;
  font-size: 0.95rem;
}

.tutorials-list__actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 4px;
}

.tutorials-list__actions .v-btn {
  color: #ffffff;
}

.tutorials-list__manage {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.tutorials-list__body {
  background: linear-gradient(180deg, #f7faf8 0%, #eef7f2 100%);
}

.tutorials-list__mode-switch {
  display: flex;
  justify-content: center;
}

.tutorials-list__mode-toggle {
  background: #ffffff;
  border: 1px solid rgba(20, 184, 166, 0.2);
  border-radius: 8px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  max-width: 430px;
  overflow: hidden;
  width: 100%;
}

.tutorials-list__mode-toggle .v-btn {
  border-radius: 0;
  color: #38545a;
  font-weight: 800;
  min-width: 0;
}

.tutorials-list__mode-toggle .v-btn--active {
  background: #123b3a !important;
  color: #ffffff !important;
}

.tutorials-list__tab-logo {
  align-items: center;
  background: rgba(255, 255, 255, 0.86);
  border-radius: 999px;
  display: inline-flex;
  height: 19px;
  justify-content: center;
  margin-right: 6px;
  overflow: hidden;
  width: 19px;
}

.tutorials-list__tab-logo img {
  display: block;
  height: 17px;
  object-fit: contain;
  width: 17px;
}

.tutorials-list__chat-shell {
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(15, 35, 39, 0.1);
  border-radius: 8px;
  box-shadow: 0 18px 48px rgba(15, 35, 39, 0.1);
  overflow: hidden;
}

.tutorials-list__chat-header {
  align-items: center;
  background: linear-gradient(
      135deg,
      rgba(18, 59, 58, 0.98),
      rgba(22, 91, 82, 0.92)
    ),
    #123b3a;
  color: #ffffff;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 14px;
}

.tutorials-list__chat-title {
  font-size: 0.98rem;
  font-weight: 900;
  line-height: 1.25;
}

.tutorials-list__chat-subtitle {
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.78rem;
  line-height: 1.35;
  margin-top: 3px;
}

.tutorials-list__chat-processes {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: #ffffff !important;
  flex: 0 0 auto;
}

.tutorials-list__chat-messages {
  background: linear-gradient(
      180deg,
      rgba(230, 246, 241, 0.72),
      rgba(255, 255, 255, 0.96)
    ),
    #f8fbfa;
  max-height: min(50vh, 430px);
  min-height: 300px;
  overflow-y: auto;
  padding: 16px;
}

.tutorials-list__chat-message {
  align-items: flex-start;
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
}

.tutorials-list__chat-message--user {
  flex-direction: row-reverse;
}

.tutorials-list__chat-avatar {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(20, 184, 166, 0.18);
  border-radius: 999px;
  color: #0f766e;
  display: inline-flex;
  flex: 0 0 auto;
  height: 32px;
  justify-content: center;
  width: 32px;
}

.tutorials-list__chat-avatar img {
  display: block;
  height: 24px;
  object-fit: contain;
  width: 24px;
}

.tutorials-list__chat-message--user .tutorials-list__chat-avatar {
  background: #fff7df;
  border-color: rgba(242, 193, 78, 0.34);
  color: #8a6415;
}

.tutorials-list__chat-bubble {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  border-top-left-radius: 4px;
  box-shadow: 0 10px 26px rgba(15, 35, 39, 0.06);
  color: #24343c;
  max-width: min(720px, calc(100% - 42px));
  padding: 11px 12px;
}

.tutorials-list__chat-message--user .tutorials-list__chat-bubble {
  background: #123b3a;
  border-color: rgba(18, 59, 58, 0.36);
  border-radius: 12px;
  border-top-right-radius: 4px;
  color: #ffffff;
}

.tutorials-list__chat-bubble--loading {
  align-items: center;
  background: #fbfdfc;
  color: #4c646d;
  display: inline-flex;
  gap: 10px;
}

.tutorials-list__thinking {
  align-items: center;
  background: #e8f7f2;
  border: 1px solid rgba(20, 184, 166, 0.22);
  border-radius: 999px;
  display: inline-flex;
  gap: 4px;
  height: 24px;
  justify-content: center;
  width: 42px;
}

.tutorials-list__thinking span {
  animation: caju-thinking 1.1s ease-in-out infinite;
  background: #0f766e;
  border-radius: 999px;
  display: block;
  height: 5px;
  opacity: 0.35;
  width: 5px;
}

.tutorials-list__thinking span:nth-child(2) {
  animation-delay: 0.16s;
}

.tutorials-list__thinking span:nth-child(3) {
  animation-delay: 0.32s;
}

.tutorials-list__chat-text {
  font-size: 0.86rem;
  line-height: 1.48;
  white-space: pre-line;
  word-break: break-word;
}

.tutorials-list__chat-suggestions {
  margin-top: 10px;
}

.tutorials-list__chat-label {
  color: #657780;
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 0;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.tutorials-list__chat-message--user .tutorials-list__chat-label {
  color: rgba(255, 255, 255, 0.72);
}

.tutorials-list__chat-suggestion-list {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.tutorials-list__chat-suggestion {
  border-color: rgba(20, 184, 166, 0.34) !important;
  color: #0f766e !important;
  max-width: 100%;
}

.tutorials-list__chat-suggestion ::v-deep .v-btn__content {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tutorials-list__support-link {
  background: #f2c14e !important;
  color: #16343a !important;
  font-weight: 800;
  margin-top: 10px;
}

.tutorials-list__chat-composer {
  align-items: flex-end;
  background: #ffffff;
  border-top: 1px solid rgba(15, 35, 39, 0.08);
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr auto;
  padding: 12px;
}

.tutorials-list__chat-composer ::v-deep .v-input__slot {
  background: #fbfdfc !important;
  border-color: rgba(20, 184, 166, 0.24) !important;
}

.tutorials-list__chat-send {
  background: #123b3a !important;
  color: #ffffff !important;
  min-height: 40px;
}

@keyframes caju-thinking {
  0%,
  80%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }
  40% {
    opacity: 1;
    transform: translateY(-3px);
  }
}

.tutorials-list__process-panel {
  min-height: 320px;
}

.tutorials-list__search-panel {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 8px;
  box-shadow: 0 16px 42px rgba(15, 35, 39, 0.08);
  padding: 14px;
}

.tutorials-list__search-input ::v-deep .v-input__slot {
  background: #fbfdfc !important;
}

.tutorials-list__scope {
  background: #eef7f4;
  border: 1px solid rgba(20, 184, 166, 0.18);
  border-radius: 8px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  overflow: hidden;
  width: 100%;
}

.tutorials-list__scope .v-btn {
  border-radius: 0;
  color: #38545a;
  font-weight: 700;
  min-width: 0;
}

.tutorials-list__scope .v-btn--active {
  background: #123b3a !important;
  color: #ffffff !important;
}

.tutorials-list__category-rail {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.tutorials-list__category-rail .v-chip {
  border-color: rgba(20, 184, 166, 0.32) !important;
  color: #31545b;
  font-weight: 700;
}

.tutorials-list__category--active {
  background: #dff6ef !important;
  color: #0f766e !important;
}

.tutorials-list__clear {
  color: #63727a;
  margin-left: auto;
}

.tutorials-list__result-row {
  align-items: center;
  color: #667985;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.78rem;
  gap: 10px;
  justify-content: space-between;
  margin-top: 12px;
}

.tutorials-list__items {
  max-height: min(58vh, 560px);
  overflow-y: auto;
  padding: 2px 2px 4px;
}

.tutorials-list__item {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 8px;
  margin-bottom: 8px;
  min-height: 78px;
  transition: border-color 160ms ease, box-shadow 160ms ease,
    transform 160ms ease;
}

.tutorials-list__item:hover {
  border-color: rgba(20, 184, 166, 0.32);
  box-shadow: 0 10px 28px rgba(15, 35, 39, 0.08);
  transform: translateY(-1px);
}

.tutorials-list__item--current {
  border-color: rgba(242, 193, 78, 0.52);
}

.tutorials-list__item-avatar {
  background: #e7f8f3;
  color: #0f766e;
}

.tutorials-list__item-avatar .v-icon {
  color: #0f766e;
}

.tutorials-list__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.tutorials-list__empty {
  background: #ffffff;
  border: 1px dashed rgba(20, 184, 166, 0.3);
  border-radius: 8px;
  color: #667985;
  margin-top: 14px;
  padding: 44px 16px;
  text-align: center;
}

.tutorials-list__empty-logo {
  align-items: center;
  background: #e7f8f3;
  border: 1px solid rgba(20, 184, 166, 0.18);
  border-radius: 8px;
  display: inline-flex;
  height: 42px;
  justify-content: center;
  margin-bottom: 10px;
  overflow: hidden;
  width: 42px;
}

.tutorials-list__empty-logo img {
  display: block;
  height: 34px;
  object-fit: contain;
  width: 34px;
}

@media (max-width: 720px) {
  .tutorials-list__header {
    flex-direction: column;
  }

  .tutorials-list__hero {
    align-items: flex-start;
  }

  .tutorials-list__agent-mark {
    height: 44px;
    width: 44px;
  }

  .tutorials-list__agent-mark img {
    height: 36px;
    width: 36px;
  }

  .tutorials-list__actions {
    justify-content: space-between;
    width: 100%;
  }

  .tutorials-list__scope {
    grid-template-columns: 1fr;
  }

  .tutorials-list__chat-header,
  .tutorials-list__chat-composer {
    grid-template-columns: 1fr;
  }

  .tutorials-list__chat-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .tutorials-list__chat-processes,
  .tutorials-list__chat-send {
    width: 100%;
  }

  .tutorials-list__chat-messages {
    max-height: 48vh;
    min-height: 280px;
    padding: 12px;
  }

  .tutorials-list__chat-bubble {
    max-width: calc(100% - 38px);
  }

  .tutorials-list__clear {
    margin-left: 0;
  }
}
</style>
